import { FC, useState, useEffect, useCallback } from 'react';

import {
  getReliabilityStats,
  resetReliabilityProfile,
} from '@app/widgets/canvas/utils/sse-connection-strategy';

// LocalStorage ключи - ЕДИНЫЙ КЛЮЧ для Canvas SSE и T2S SSE
const STORAGE_KEYS = {
  BLOCK_SSE: 'sse-debug-block',
  PANEL_VISIBLE: 'sse_debug_panel_visible',
} as const;

// Инициализируем состояние из localStorage
const getInitialBlockState = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.BLOCK_SSE);
    return stored === 'true';
  } catch {
    return false;
  }
};

// Глобальное состояние для принудительного включения recovery mode
// Это позволяет тестировать recovery без реального зависания SSE
interface RecoveryDebugState {
  forceRecoveryMode: boolean;
  blockSSEConnection: boolean;
  listeners: Set<() => void>;
}

const globalDebugState: RecoveryDebugState = {
  forceRecoveryMode: false,
  blockSSEConnection: getInitialBlockState(),
  listeners: new Set(),
};

// Подписка на изменения состояния
const subscribeToDebugState = (listener: () => void) => {
  globalDebugState.listeners.add(listener);
  return () => {
    globalDebugState.listeners.delete(listener);
  };
};

// Уведомление подписчиков
const notifyListeners = () => {
  globalDebugState.listeners.forEach((listener) => listener());
};

// API для проверки состояния блокировки
// ВАЖНО: Читаем из localStorage КАЖДЫЙ РАЗ, потому что модуль может кешироваться
export const isSSEConnectionBlocked = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.BLOCK_SSE);
    const isBlocked = stored === 'true';
    // Синхронизируем глобальное состояние
    globalDebugState.blockSSEConnection = isBlocked;
    return isBlocked;
  } catch {
    return false;
  }
};

// API для проверки принудительного recovery
export const isForceRecoveryMode = (): boolean => {
  return globalDebugState.forceRecoveryMode;
};

/**
 * Компонент для отображения статистики надежности SSE
 */
const SSEReliabilityStats: FC = () => {
  const [stats, setStats] = useState(() => getReliabilityStats());

  // Обновляем статистику каждые 2 секунды
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(getReliabilityStats());
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const { profile, timeout, successRate } = stats;
  const totalConnections = profile.successfulConnections + profile.failedConnections;

  // Цвет в зависимости от уровня надежности
  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'reliable':
        return '#4ecca3';
      case 'unknown':
        return '#ffd700';
      case 'unstable':
        return '#ff9800';
      case 'problematic':
        return '#e94560';
      default:
        return '#888';
    }
  };

  const getLevelText = (level: string): string => {
    switch (level) {
      case 'reliable':
        return 'Надежное';
      case 'unknown':
        return 'Неизвестно';
      case 'unstable':
        return 'Нестабильное';
      case 'problematic':
        return 'Проблемное';
      default:
        return level;
    }
  };

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '8px',
        background: '#16213e',
        borderRadius: '4px',
        border: '1px solid #0f3460',
      }}
    >
      <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#e94560', fontSize: '11px' }}>
        📊 SSE Reliability Profile
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ color: '#888' }}>Уровень:</span>
        <span style={{ color: getLevelColor(profile.reliabilityLevel), fontWeight: 'bold' }}>
          {getLevelText(profile.reliabilityLevel)}
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ color: '#888' }}>Таймаут:</span>
        <span style={{ color: '#4ecca3' }}>{timeout}ms</span>
      </div>

      {totalConnections > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ color: '#888' }}>Успешных:</span>
            <span style={{ color: '#4ecca3' }}>{profile.successfulConnections}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ color: '#888' }}>Неудачных:</span>
            <span style={{ color: '#e94560' }}>{profile.failedConnections}</span>
          </div>

          {successRate !== null && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>Успешность:</span>
              <span style={{ color: getLevelColor(profile.reliabilityLevel) }}>
                {successRate.toFixed(1)}%
              </span>
            </div>
          )}
        </>
      )}

      {totalConnections === 0 && (
        <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
          Нет истории подключений
        </div>
      )}
    </div>
  );
};

/**
 * Панель для отладки SSE Recovery Mode
 * Позволяет:
 * - Эмулировать зависание SSE на CONNECTING
 * - Принудительно включать recovery mode в работу
 * - Сохранять состояние в localStorage
 * - Перезагружать страницу при смене режима SSE
 */
export const SSERecoveryDebugPanel: FC = () => {
  // Загружаем состояние видимости из localStorage
  const [isVisible, setIsVisible] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PANEL_VISIBLE);
    return saved === null ? true : saved === 'true';
  });

  const [forceRecovery, setForceRecovery] = useState(globalDebugState.forceRecoveryMode);
  const [blockSSE, setBlockSSE] = useState(globalDebugState.blockSSEConnection);
  const [logs, setLogs] = useState<string[]>([]);

  // Сохраняем состояние видимости при изменении
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PANEL_VISIBLE, String(isVisible));
  }, [isVisible]);

  // Подписываемся на изменения
  useEffect(() => {
    const unsubscribe = subscribeToDebugState(() => {
      setForceRecovery(globalDebugState.forceRecoveryMode);
      setBlockSSE(globalDebugState.blockSSEConnection);
    });
    return unsubscribe;
  }, []);

  // Перехватываем консольные логи recovery
  useEffect(() => {
    const originalWarn = console.warn;
    const originalInfo = console.info;

    const captureLog = (level: string, args: unknown[]) => {
      const message = args
        .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
        .join(' ');

      if (
        message.includes('[SSE Recovery]') ||
        message.includes('[T2S SSE]') ||
        message.includes('[SSE Diag]') ||
        message.includes('Recovery mode')
      ) {
        setLogs((prev) => [...prev.slice(-20), `[${level}] ${message}`]);
      }
    };

    console.warn = (...args: unknown[]) => {
      captureLog('WARN', args);
      originalWarn.apply(console, args);
    };

    console.info = (...args: unknown[]) => {
      captureLog('INFO', args);
      originalInfo.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
      console.info = originalInfo;
    };
  }, []);

  const handleToggleBlockSSE = useCallback(() => {
    const newValue = !globalDebugState.blockSSEConnection;
    globalDebugState.blockSSEConnection = newValue;

    // Сохраняем в localStorage
    localStorage.setItem(STORAGE_KEYS.BLOCK_SSE, String(newValue));

    notifyListeners();
    console.warn('[SSE Recovery Debug] Block SSE connection:', newValue);

    // Перезагружаем страницу для применения изменений
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  const handleForceRecovery = useCallback(() => {
    globalDebugState.forceRecoveryMode = true;
    notifyListeners();
    console.warn('[SSE Recovery Debug] Force recovery mode ACTIVATED');

    // Автоматически сбрасываем через 30 секунд
    setTimeout(() => {
      globalDebugState.forceRecoveryMode = false;
      notifyListeners();
      console.info('[SSE Recovery Debug] Force recovery mode auto-deactivated');
    }, 30000);
  }, []);

  const handleClearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const handleResetAll = useCallback(() => {
    globalDebugState.forceRecoveryMode = false;
    globalDebugState.blockSSEConnection = false;

    // Очищаем localStorage
    localStorage.removeItem(STORAGE_KEYS.BLOCK_SSE);

    // Сбрасываем профиль надежности
    resetReliabilityProfile();

    notifyListeners();
    setLogs([]);
    console.info('[SSE Recovery Debug] All debug states reset (including reliability profile)');

    // Перезагружаем страницу для сброса SSE
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          zIndex: 99999,
          padding: '8px 16px',
          background: blockSSE ? '#e94560' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px',
          boxShadow: blockSSE ? '0 0 10px rgba(233, 69, 96, 0.5)' : 'none',
        }}
      >
        SSE Debug
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        zIndex: 99999,
        width: '400px',
        maxHeight: '88dvh',
        background: '#1a1a2e',
        color: '#eee',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        fontFamily: 'monospace',
        fontSize: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px 15px',
          background: '#16213e',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #0f3460',
        }}
      >
        <span style={{ fontWeight: 'bold', color: '#e94560' }}>SSE Recovery Debug Panel</span>
        <button
          onClick={() => setIsVisible(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#888',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          ×
        </button>
      </div>

      {/* Controls */}
      <div style={{ padding: '15px', borderBottom: '1px solid #0f3460' }}>
        <div style={{ marginBottom: '10px', color: '#888' }}>Эмуляция проблем с SSE:</div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handleToggleBlockSSE}
            style={{
              padding: '8px 12px',
              background: blockSSE ? '#e94560' : '#0f3460',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: '1',
            }}
          >
            {blockSSE ? '🔴 SSE Blocked' : 'Block SSE'}
          </button>

          <button
            onClick={handleForceRecovery}
            style={{
              padding: '8px 12px',
              background: forceRecovery ? '#4ecca3' : '#0f3460',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: '1',
            }}
          >
            {forceRecovery ? '✅ Recovery Active' : 'Force Recovery'}
          </button>

          <button
            onClick={handleResetAll}
            style={{
              padding: '8px 12px',
              background: '#333',
              color: '#fff',
              border: '1px solid #555',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
        </div>

        {/* Status */}
        <div
          style={{ marginTop: '12px', padding: '8px', background: '#0f3460', borderRadius: '4px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>SSE Connection:</span>
            <span style={{ color: blockSSE ? '#e94560' : '#4ecca3' }}>
              {blockSSE ? 'BLOCKED (simulating hang)' : 'Normal'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Recovery Mode:</span>
            <span style={{ color: forceRecovery ? '#4ecca3' : '#888' }}>
              {forceRecovery ? 'ACTIVE (polling)' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* SSE Reliability Stats */}
        <SSEReliabilityStats />

        <div style={{ marginTop: '8px', color: '#666', fontSize: '10px' }}>
          Block SSE - эмулирует зависание на CONNECTING (reload страницы).
          <br />
          Force Recovery - немедленно включает polling (30 сек).
          <br />
          Состояние сохраняется в localStorage.
        </div>
      </div>

      {/* Logs */}
      <div
        style={{
          padding: '10px 15px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}
        >
          <span style={{ color: '#888' }}>Logs ({logs.length}):</span>
          <button
            onClick={handleClearLogs}
            style={{
              padding: '4px 8px',
              background: '#333',
              color: '#888',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '10px',
            }}
          >
            Clear
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            background: '#0a0a1a',
            padding: '8px',
            borderRadius: '4px',
            fontSize: '10px',
            lineHeight: '1.4',
          }}
        >
          {logs.length === 0 ? (
            <div style={{ color: '#444' }}>No SSE recovery logs yet...</div>
          ) : (
            logs.map((log, i) => (
              <div
                key={i}
                style={{
                  color: log.includes('WARN') ? '#e94560' : '#4ecca3',
                  borderBottom: '1px solid #1a1a2e',
                  paddingBottom: '4px',
                  marginBottom: '4px',
                  wordBreak: 'break-all',
                }}
              >
                {log}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
